import { isEqual } from 'lodash-es'
import {
  DeploymentAlertSetting,
  DeploymentAlertSettingPayload
} from '@/types/deploymentAlert'

// Matches the server default ALLOWED_CHAT_WEBHOOK_HOSTS. The browser cannot
// repeat the DNS/SSRF checks; the API still rejects anything off-allowlist.
const ALLOWED_CHAT_WEBHOOK_HOSTS = ['chat.googleapis.com']

// `https://` alone passes a startsWith check but is not a usable endpoint, so
// parse it and require an allowed host.
export const isValidWebhookUrl = (value: string) => {
  try {
    const url = new URL(value)

    return (
      url.protocol === 'https:' &&
      ALLOWED_CHAT_WEBHOOK_HOSTS.includes(url.hostname.toLowerCase())
    )
  } catch {
    return false
  }
}

export type NotificationRuleFormValues = {
  // Stable across re-renders so a removed rule cannot make React reuse the
  // wrong card's state. Client-side only — never sent to the API.
  uid: string
  // Absent until the rule has been saved for the first time.
  id?: string
  device_ids: string[]
  emails: string[]
  chat_webhooks: string[]
}

export type SavedNotificationRuleFormValues = NotificationRuleFormValues & {
  id: string
}

export type NotificationsFormValues = {
  rules: NotificationRuleFormValues[]
}

let ruleUidCounter = 0

const nextRuleUid = () => `rule-${(ruleUidCounter += 1)}`

export const createEmptyRule = (): NotificationRuleFormValues => ({
  uid: nextRuleUid(),
  device_ids: [],
  emails: [],
  chat_webhooks: []
})

export const toRulePayload = (
  rule: NotificationRuleFormValues | DeploymentAlertSetting
): DeploymentAlertSettingPayload => ({
  device_ids: rule.device_ids,
  emails: rule.emails,
  chat_webhooks: rule.chat_webhooks
})

export const toFormValues = (
  settings: DeploymentAlertSetting[]
): NotificationsFormValues => ({
  rules:
    settings.length > 0
      ? settings.map((setting) => ({
          uid: nextRuleUid(),
          id: setting.id,
          device_ids: setting.device_ids,
          emails: setting.emails,
          chat_webhooks: setting.chat_webhooks
        }))
      : [createEmptyRule()]
})

export type NotificationRuleChanges = {
  created: NotificationRuleFormValues[]
  updated: SavedNotificationRuleFormValues[]
  deletedIds: string[]
}

export type CreatedRuleAssignment = {
  uid: string
  id: string
}

export const idsFromSettledCreates = (
  created: NotificationRuleFormValues[],
  results: PromiseSettledResult<DeploymentAlertSetting>[]
): CreatedRuleAssignment[] =>
  created.flatMap((rule, index) => {
    const result = results[index]

    if (result?.status !== 'fulfilled' || !result.value.id) {
      return []
    }

    return [{ uid: rule.uid, id: result.value.id }]
  })

const isSavedRule = (
  rule: NotificationRuleFormValues
): rule is SavedNotificationRuleFormValues => !!rule.id

// The API is per-rule CRUD but the screen saves every rule at once, so the single
// save button is resolved into the individual creates/updates/deletes it implies.
export const buildRuleChanges = (
  initialSettings: DeploymentAlertSetting[],
  rules: NotificationRuleFormValues[]
): NotificationRuleChanges => {
  const savedRules = rules.filter(isSavedRule)
  const keptIds = savedRules.map((rule) => rule.id)

  const created = rules.filter((rule) => !rule.id)

  const updated = savedRules.filter((rule) => {
    const initial = initialSettings.find((setting) => setting.id === rule.id)

    return initial && !isEqual(toRulePayload(rule), toRulePayload(initial))
  })

  const deletedIds = initialSettings
    .filter((setting) => !keptIds.includes(setting.id))
    .map((setting) => setting.id)

  return { created, updated, deletedIds }
}
